import { useState } from 'react';
import { Box, CardMedia, Chip, Typography } from '@mui/material';
import { AutoAwesome, PlayArrow } from '@mui/icons-material';
import { colors, heroGradient } from '../styles';

interface FeatureMediaProps {
    src?: string;
    alt: string;
    height: number | Record<string, number>;
    /** תג פינתי מעל התמונה */
    badge?: string;
    /** הזחה פנימה של התג — כשיש סרט AI באותו צד */
    badgeInsetEnd?: number;
    /** מציג כפתור נגן מעל התמונה */
    showPlayOverlay?: boolean;
    /** תמונה אפורה — ליכולות שעוד לא זמינות */
    muted?: boolean;
}

/**
 * תמונת יכולת מ-S3. אם הקובץ עוד לא הועלה (או נחסם) מוצג רקע מדורג עם שם
 * הקטגוריה במקום תמונה שבורה.
 */
const FeatureMedia = ({ src, alt, height, badge, badgeInsetEnd = 16, showPlayOverlay, muted }: FeatureMediaProps) => {
    const [failed, setFailed] = useState(false);
    const showFallback = !src || failed;

    return (
        <Box sx={{ position: 'relative', bgcolor: colors.softBg, height, overflow: 'hidden', filter: muted ? 'grayscale(1)' : undefined }}>
            {showFallback ? (
                <Box
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        color: '#ffffff',
                        background: heroGradient
                    }}
                >
                    <AutoAwesome sx={{ fontSize: 42, opacity: 0.9 }} />
                    <Typography sx={{ fontWeight: 800, opacity: 0.9 }}>{badge || alt}</Typography>
                </Box>
            ) : (
                <CardMedia
                    component="img"
                    image={src}
                    alt={alt}
                    loading="lazy"
                    draggable={false}
                    onError={() => setFailed(true)}
                    sx={{
                        height: '100%',
                        objectFit: 'contain',
                        objectPosition: 'top',
                        p: 1,
                        WebkitUserDrag: 'none',
                        pointerEvents: 'none'
                    }}
                />
            )}

            {badge && !showFallback && (
                <Chip
                    label={badge}
                    sx={{
                        position: 'absolute',
                        top: 16,
                        insetInlineEnd: badgeInsetEnd,
                        bgcolor: 'rgba(8, 24, 45, 0.82)',
                        color: '#ffffff',
                        fontWeight: 700,
                        backdropFilter: 'blur(6px)'
                    }}
                />
            )}

            {showPlayOverlay && (
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none'
                    }}
                >
                    <Box
                        sx={{
                            width: 66,
                            height: 66,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(8, 24, 45, 0.72)',
                            border: '2px solid rgba(255,255,255,0.85)',
                            boxShadow: '0 10px 30px rgba(8, 24, 45, 0.35)'
                        }}
                    >
                        <PlayArrow sx={{ color: '#ffffff', fontSize: 38 }} />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default FeatureMedia;
